import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { decryptToken } from '../../../Utlis/Encryption';
import AbsenceAPI from '../../../services/absenceApi';
import { toast } from 'react-hot-toast';
import TruncatedCell from '../../../components/TruncatedCell';

const localizer = momentLocalizer(moment);

const AbsenceCalendar = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legend, setLegend] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());

  const rawEncryptedToken = localStorage.getItem('token');
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  useEffect(() => {
    if (token) {
      loadCalendar();
    }
  }, [currentDate]);

  const loadCalendar = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const api = new AbsenceAPI(token);
      
      // Get start and end of current month
      const startDate = moment(currentDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(currentDate).endOf('month').format('YYYY-MM-DD');

      let response;
      let absences = [];
      let legendData = [];

      // Try calendar endpoint first
      try {
        response = await api.getMyAbsenceCalendar(startDate, endDate, 'month');
        
        // Handle both response formats: status === 'success' or error === false
        if (response.status === 'success' || (response.error === false && response.data)) {
          const calendarData = response.data || response;
          
          // Set legend
          if (calendarData.legend) {
            legendData = calendarData.legend;
            setLegend(legendData);
          }

          // Convert calendar days to events
          if (calendarData.calendar?.days) {
            const calendarEvents = [];
            calendarData.calendar.days.forEach((day) => {
              day.absences?.forEach((absence, idx) => {
                // Parse the date from the day object
                const dayDate = moment(day.date || day).startOf('day');
                const eventStart = dayDate.toDate();
                const eventEnd = dayDate.endOf('day').toDate();
                
                calendarEvents.push({
                  id: `${day.date || day}-${absence.type?.code || 'absence'}-${idx}`,
                  title: absence.type?.name || 'Absence',
                  start: eventStart,
                  end: eventEnd,
                  type: absence.type,
                  comment: absence.comment,
                  color: absence.type?.color || '#9CA3AF',
                  isPaid: absence.type?.isPaid || false,
                });
              });
            });
            setEvents(calendarEvents);
            return;
          }
        }
      } catch (calendarError) {
        console.log('Calendar endpoint failed, trying absences endpoint:', calendarError);
      }

      // Fallback: Use absences endpoint and convert to calendar events
      response = await api.getMyAbsences({
        start_date: startDate,
        end_date: endDate,
      });

      if (response.status === 'success' || (response.error === false && response.data)) {
        absences = response.data?.absences || response.data || [];
        if (!Array.isArray(absences)) absences = [];

        // Build legend from unique absence types
        const typeMap = new Map();
        absences.forEach((absence) => {
          if (absence.type && !typeMap.has(absence.type.code)) {
            typeMap.set(absence.type.code, absence.type);
          }
        });
        legendData = Array.from(typeMap.values());
        setLegend(legendData);

        // Convert absences to calendar events
        const calendarEvents = [];
        absences.forEach((absence) => {
          if (absence.dateRange?.start && absence.dateRange?.end) {
            // Parse date range - handle ISO strings with time
            const startMoment = moment(absence.dateRange.start);
            const endMoment = moment(absence.dateRange.end);
            
            // Create events for each day in the range
            let currentDate = startMoment.clone();
            while (currentDate.isSameOrBefore(endMoment, 'day')) {
              const eventStart = currentDate.startOf('day').toDate();
              const eventEnd = currentDate.endOf('day').toDate();
              
              calendarEvents.push({
                id: `${absence.id}-${currentDate.format('YYYY-MM-DD')}`,
                title: absence.type?.name || 'Absence',
                start: eventStart,
                end: eventEnd,
                type: absence.type,
                comment: absence.comment,
                color: absence.type?.color || '#9CA3AF',
                isPaid: absence.type?.isPaid || false,
              });
              
              currentDate.add(1, 'day');
            }
          }
        });
        
        setEvents(calendarEvents);
      }
    } catch (error) {
      console.error('Error loading calendar:', error);
      toast.error(error.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color || '#9CA3AF',
        color: 'white',
        borderRadius: '6px',
        border: '0px',
        display: 'block',
        paddingLeft: '4px',
        opacity: 0.9,
      },
    };
  };

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  return (
    <Box>
      {/* Legend */}
      {/* {legend.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('absence.legend')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {legend.map((type) => (
              <Box key={type.code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '4px',
                    backgroundColor: type.color,
                  }}
                />
                <Typography variant="body2">{type.name}</Typography>
                {type.isPaid && (
                  <Chip
                    label="Paid"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '9px',
                      backgroundColor: '#E8F5E9',
                      color: '#2E7D32',
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )} */}

      {/* Calendar */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ height: '80vh', background: '#fff', borderRadius: '8px' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Calendar
              localizer={localizer}
              events={events}
              defaultView="month"
              views={['day', 'week', 'month']}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              eventPropGetter={eventStyleGetter}
              onNavigate={handleNavigate}
              date={currentDate}
              components={{
                event: ({ event }) => (
                  <span>
                    <TruncatedCell text={event.title} sx={{ color: 'white' }} />
                    {event.comment && (
                      <>
                        <br />
                        <small style={{ fontSize: '10px', opacity: 0.9 }}>
                          {event.comment}
                        </small>
                      </>
                    )}
                  </span>
                ),
              }}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AbsenceCalendar;
